/**
 * One purchase: the schedule, the receipts, and where it stands.
 *
 * **The screen clients ring about**, so what they ask is at the top: what have I paid, what is
 * left, and when is the next one. Everything a member of staff has to *do* is beneath that,
 * because they are the ones on the phone and the buyer is the one asking.
 *
 * The statement is the same numbers arranged for the buyer, and it prints — half of them will
 * want it on paper.
 */

import { useRef, useState, type SVGProps } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formValues, useApiMutation } from "@/lib/mutations";
import { fileSize, minor, titleCase, useFormat } from "@/lib/format";
import { primaryActionFor, spineFor, stageOf } from "@/lib/sales";
import { useSoldNoun } from "@/lib/development";
import { useSession } from "@/lib/session";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { MoneyInput } from "@/components/money-input";
import { IconContract, IconExternal, IconTrash } from "@/components/icons";
import { Refusal } from "@/components/padlock";
import { SALE_DOCUMENT_INFO, SALE_DOCUMENT_KINDS } from "@rentorng/core";

interface Instalment {
  id: string;
  sequence: number;
  label: string;
  dueDate: string;
  amount: number;
  paid: number;
  lateFee: number;
  amountLabel: string;
  paidLabel: string;
  status: "due" | "part_paid" | "paid" | "waived" | "overdue";
  waivedReason: string | null;
}

interface Detail {
  purchase: {
    id: string;
    reference: string;
    status: string;
    price: number;
    startDate: string;
    frequency: string;
    instalments: number;
    lateFeeBps: number;
    earlyDiscountBps: number;
    allocationMethod: string | null;
    allocatedAt: string | null;
    handedOverOn: string | null;
    notes: string | null;
  };
  buyer: { id: string; name: string; phone: string | null; email: string | null };
  estate: { id: string; name: string; city: string | null };
  plot: { id: string; label: string; sizeSqm: number | null } | null;
  instalments: Instalment[];
  receipts: Array<{
    id: string;
    receiptNumber: string;
    amount: number;
    method: string;
    receivedOn: string;
    status: string;
    /* Which of these was the holding fee. It is an ordinary receipt in every other respect — same
       series, same reversal rule — and this is the one thing about it the screen has to be able to
       say, because the buyer paid it before the plan existed and will look for it. */
    reservationId: string | null;
  }>;
  /*
    Who sold it and on what terms — **as agreed on the day, never re-resolved.**

    The rate came off the property, the release, the price list or the row itself once, at the
    moment of sale, and lives on this purchase. A developer who re-rates a release next month has
    changed what the next marketer earns and has not moved this — the same promise the price makes
    against a re-priced type, and the reassurance that makes re-rating safe to use at all. Saying so
    is this screen's job.

    `full` is what the sale will earn in total, which all four bases agree on. **What it has earned
    so far is deliberately not here**: that is an arithmetic over allocations, it belongs to one
    function on the server, and a second implementation of it on this screen is exactly what
    ADR-0099 was written about. The marketer's own screen carries it, per sale.
  */
  commission: {
    marketerId: string | null;
    marketerName: string | null;
    marketerStatus: string | null;
    bp: number;
    basis: string;
    basisLabel: string;
    full: number;
    fullLabel: string;
  } | null;
  /*
    What was held before this was a sale, where it came out of a reservation at all.

    Null on a walk-in sale, which is most of them. Where it is not null the screen has to say that
    the holding fee **counted toward the price** rather than sitting on top of it — otherwise the
    buyer reads the schedule below as asking for money they have already paid.
  */
  reservation: {
    id: string;
    reference: string;
    reservedOn: string;
    feeAmount: number;
    feeAmountLabel: string;
    refundable: boolean;
    convertedAt: string | null;
    paid: number;
    paidLabel: string;
  } | null;
  standing: {
    priceLabel: string;
    paidLabel: string;
    outstandingLabel: string;
    arrearsLabel: string;
    creditLabel: string;
    arrears: number;
    credit: number;
    percentPaid: number;
    settled: boolean;
    nextDue: { sequence: number; dueDate: string; amount: number } | null;
  };
  settlement: { payableLabel: string; discountLabel: string; discount: number };
  /*
    What this sale used to be against, where it is not what it was.

    Empty for almost every purchase, and that is the point: a developer who has never re-cut a
    building never sees this. Where it is not empty it answers the only question a buyer whose
    building was split under them will ever ask — "I bought Tower A; what do I own now?" — and it
    is a list rather than a column because a four-year plan can outlive two changes of mind.
  */
  moves: Array<{
    id: string;
    movedOn: string;
    cancelled: boolean;
    reason: string | null;
    fromLabel: string | null;
    toLabel: string | null;
  }>;
}

/**
 * One change of what was bought, in a sentence.
 *
 * Written here rather than by the server because it is the same sentence on the screen and on the
 * statement, and the two must not drift. A cancellation is said as a cancellation: a buyer whose
 * sale ended should not read a euphemism about a move.
 */
function moveSentence(move: { cancelled: boolean; fromLabel: string | null; toLabel: string | null }): string {
  const from = move.fromLabel ?? "what was bought";
  if (move.cancelled) return `${from} was withdrawn and this sale was cancelled`;
  return `${from} became ${move.toLabel ?? "another"}`;
}

/**
 * A document attached to this sale.
 *
 * Private to the agency and the buyer. There is deliberately no public endpoint for these: a deed
 * carries somebody's full name and what they paid, and a survey plan is the legal extent of their
 * property. `kindLabel` is composed by the server so no screen ever prints a raw column value.
 */
interface SaleDocument {
  id: string;
  name: string;
  kind: string;
  kindLabel: string;
  mimeType: string | null;
  sizeBytes: number | null;
  createdAt: string;
  /** Null where the account that attached it has since been removed. */
  uploadedByName: string | null;
}

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

/**
 * One mark drawn here rather than imported.
 *
 * Same construction as `components/icons.tsx` — 24 grid, 1.75 stroke, round caps and joins,
 * `currentColor` — so it sits with the rest of the set, and it lives beside the one screen that
 * uses it, exactly as `agency-mark.tsx` keeps its own two.
 */
const IconUpload = ({ size = 18, ...rest }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    <path d="M12 15V4" />
    <path d="m8 8 4-4 4 4" />
    <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
  </svg>
);

const TONE: Record<Instalment["status"], "success" | "warn" | "danger" | "muted"> = {
  paid: "success",
  part_paid: "warn",
  due: "muted",
  overdue: "danger",
  waived: "muted",
};

export function PurchaseDetailPage() {
  const { id = "" } = useParams();
  const { can } = useSession();
  const fmt = useFormat();
  const [taking, setTaking] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const client = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [attaching, setAttaching] = useState(false);
  const [documentProblem, setDocumentProblem] = useState<string | null>(null);
  /* Chosen before the file picker opens rather than after it closes: the kind is what the document
     *is*, and being asked to classify a file you have already chosen is a second decision about
     something you had finished thinking about. */
  const [documentKind, setDocumentKind] = useState<string>("offer_letter");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["purchase", id],
    queryFn: () => api.get<Detail>(`/purchases/${id}`),
  });

  /*
    What this scheme calls the thing being allocated.

    The Property is the scheme; a plot, a home or a unit is what sits inside it, and this screen
    says the second word in six places. The purchase itself does not carry the scheme's setting, so
    the word is the one the account agrees on — see `useSoldNoun`, which explains why that is the
    honest answer and what it falls back to.
  */
  const noun = useSoldNoun();

  const { data: plots } = useQuery({
    queryKey: ["estate", data?.estate.id, "plots"],
    queryFn: () => api.get<{ plots: Array<{ id: string; label: string; status: string }> }>(`/estates/${data!.estate.id}`),
    enabled: Boolean(data?.estate.id) && allocating,
  });

  /* `["sales"]` as well as `["purchases"]`: the Sales list, the Overview and a buyer's page all
     read one cached pair of lists — see `useSales` — and a payment recorded here has to show on
     every figure that counted it. */
  const invalidates = [["purchase", id], ["purchases"], ["sales"], ["developer-summary"], ["payments"]] as string[][];

  const takePayment = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/purchases/${id}/payments`, input),
    invalidates,
    onSuccess: () => setTaking(false),
  });

  const allocate = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/purchases/${id}/allocate`, input),
    invalidates: [...invalidates, ["estate", data?.estate.id ?? ""]],
    onSuccess: () => setAllocating(false),
  });

  const lateFees = useApiMutation({
    mutationFn: () => api.post(`/purchases/${id}/late-fees`, {}),
    invalidates,
  });

  const handover = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/purchases/${id}/handover`, input),
    invalidates,
  });

  const { data: paperwork } = useQuery({
    queryKey: ["purchase-documents", id],
    queryFn: () => api.get<{ documents: SaleDocument[] }>(`/purchases/${id}/documents`),
  });

  const removeDocument = useApiMutation({
    mutationFn: (documentId: string) => api.delete(`/purchases/${id}/documents/${documentId}`),
    invalidates: [["purchase-documents", id]],
  });

  /**
   * Attaching one, by hand rather than through the shared client.
   *
   * `api.post` sends JSON; a scanned deed is multipart. Base64 in a JSON body would make an eight
   * megabyte scan a third bigger on the connection that is already the slow part.
   */
  async function attach(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setDocumentProblem(null);
    setAttaching(true);

    const form = new FormData();
    form.append("file", file);
    form.append("kind", documentKind);

    try {
      const response = await fetch(`/api/v1/purchases/${id}/documents`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (response.ok) await client.invalidateQueries({ queryKey: ["purchase-documents", id] });
      else {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        setDocumentProblem(payload?.error ?? "That document did not upload.");
      }
    } catch {
      setDocumentProblem("Could not reach the server. Check your connection and try again.");
    }

    setAttaching(false);
    if (fileInput.current) fileInput.current.value = "";
  }

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const { purchase, standing, buyer, estate, plot } = data;

  /*
    Where this sale stands, decided by the same function the Sales list and the Overview use.

    Not by a `switch` written here. Three screens asking "what stage is this at?" and answering it
    three times is three screens that can disagree about the same record — and the one that
    disagrees is always the one somebody is looking at.
  */
  const stage = stageOf({
    status: purchase.status,
    /* Whether any money has actually arrived. `standing` reports the figure as a label and a
       percentage rather than a number, and a percentage rounds a first small payment on a large
       plan to zero — which would leave a sale that has been paying for months reading "Allocated".
       A confirmed receipt is the fact the stage is about. */
    paid: data.receipts.some((receipt) => receipt.status === "confirmed") ? 1 : 0,
    allocatedAt: purchase.allocatedAt,
    plotLabel: plot?.label ?? null,
  });
  const spine = spineFor(stage, { reserved: Boolean(data.reservation) });
  const primary = primaryActionFor(stage, { settled: standing.settled });
  const failure = takePayment.errorMessage ?? allocate.errorMessage ?? lateFees.errorMessage ?? handover.errorMessage;
  /* The thrown object beside its message, so a refusal that named a plan can draw the padlock and
     the way out of it rather than a red box saying no. `Refusal` makes that decision once. */
  const refusal = takePayment.error ?? allocate.error ?? lateFees.error ?? handover.error;

  return (
    <div>
      <PageHeader
        title={buyer.name}
        back={{ to: "/app/development/sales", label: "Sales" }}
        subtitle={`${purchase.reference} · ${estate.name}${plot ? ` · ${plot.label}` : ""}`}
        description="The agreed terms, the amount received to date and the balance outstanding."
        actions={
          <>
            {/* Produced from this purchase's own schedule, so it cannot disagree with the
                statement beside it. */}
            <a className="btn btn-secondary btn-sm" href={`/app/development/sales/${id}/offer-letter`}>
              <IconContract size={15} />
              Offer letter
            </a>
            <a className="btn btn-secondary btn-sm" href={`/app/development/sales/${id}/statement`}>
              Statement
            </a>
          </>
        }
      />

      {failure ? (
        <div className="mb-4">
          <Refusal error={refusal} message={failure} />
        </div>
      ) : null}

      {/* What the buyer asks, before anything staff have to do. */}
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Price</p>
          <p className="mt-1 text-2xl font-semibold">{standing.priceLabel}</p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Received</p>
          <p className="mt-1 text-2xl font-semibold">{standing.paidLabel}</p>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink-100">
            {/* A bar somebody can read across a room. The number beside it is the same figure, for
                anybody who wants it exactly. */}
            <div className="h-full rounded-full bg-brand-600" style={{ width: `${standing.percentPaid}%` }} />
          </div>
          <p className="mt-1 text-xs text-ink-500">{standing.percentPaid}% of the price</p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Outstanding</p>
          <p className="mt-1 text-2xl font-semibold">{standing.outstandingLabel}</p>
          {standing.credit > 0 ? <p className="text-xs text-success-700">{standing.creditLabel} in credit</p> : null}
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">In arrears</p>
          <p className={`mt-1 text-2xl font-semibold ${standing.arrears > 0 ? "text-danger-600" : ""}`}>{standing.arrearsLabel}</p>
          {standing.nextDue ? (
            <p className="text-xs text-ink-500">Next due {standing.nextDue.dueDate}</p>
          ) : (
            <p className="text-xs text-ink-500">Nothing outstanding</p>
          )}
        </Card>
      </div>

      {!plot ? (
        <div className="mb-6">
          {/* The ordinary state for months, not a mistake — so it is a note rather than a warning. */}
          <Alert tone="info" title={`No ${noun.one} allocated.`}>
            This buyer has bought into {estate.name} but has not been given a particular {noun.one}. That often follows
            the money, and sometimes a ballot.
          </Alert>
        </div>
      ) : null}

      {/*
        Said on the screen as well as on the statement.

        A sale that has been moved looks completely ordinary otherwise — the reference, the price
        and the schedule are all unchanged, which is deliberate (a move never re-prices an
        agreement) and is exactly why the change has to be stated. Without this the screen says the
        buyer holds Apt 402 and nothing anywhere says they did not buy it.
      */}
      {data.moves.length ? (
        <div className="mb-6">
          <Alert tone="info" title={`What ${buyer.name} bought has changed.`}>
            <ul className="mt-1 space-y-1" data-moves>
              {data.moves.map((move) => (
                <li key={move.id}>
                  <span className="font-medium">{move.movedOn}</span> — {moveSentence(move)}
                  {move.reason ? `. ${move.reason}` : "."}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs">
              The agreed price is unchanged by any of this. It stands at {standing.priceLabel}, the figure it was agreed
              at.
            </p>
          </Alert>
        </div>
      ) : null}

      {/*
        Where this sale came out of a reservation.

        Said as plainly as it can be said, because the sentence that matters is the counter-intuitive
        one: the holding fee is part of the price and not an addition to it. Without it a buyer
        reading the schedule below concludes they are being asked for the fee a second time, and
        that is a phone call rather than a misunderstanding.
      */}
      {data.reservation ? (
        <div className="mb-6">
          <Alert tone="info" title={`Reserved on ${data.reservation.reservedOn} — ${data.reservation.reference}.`}>
            {data.reservation.paid > 0
              ? `${data.reservation.paidLabel} was received as a holding fee and has been applied to this plan as its first payment. It counts toward the price below rather than on top of it.`
              : "No holding fee was received against that reservation, so the whole price is on the schedule below."}{" "}
            <Link className="font-semibold underline" to={`/app/development/sales/reservations/${data.reservation.id}`}>
              Open the reservation
            </Link>
          </Alert>
        </div>
      ) : null}

      {standing.settled && purchase.status !== "handed_over" ? (
        <div className="mb-6">
          <Alert tone="success" title="Payment plan complete.">
            Everything on this plan is settled. Handing over is a separate act with a date of its own — money arriving is
            not the same as a {noun.one} being delivered.
          </Alert>
        </div>
      ) : null}

      {/* ------------------------------------------------------- the spine */}

      {/*
        **A spine, not a button bar.**

        The stages run down it, with what is done behind and what is next ahead, and **one primary
        action at a time** — Convert on a hold, Allocate on a sale with no unit, Record a payment on
        a live plan, Hand over on a settled one. Everything else is secondary.

        The plan says why in as many words: *"A row of six buttons of equal weight is precisely what
        makes the present version read as a database rather than as a job."* Six controls of equal
        weight forces the reader to work out which one their situation calls for, every time, on a
        screen they open twenty times a day. The spine answers that before they ask.

        **A sale may start at any stage.** Walk-in buyers exist, so a sale entered at the desk begins
        at Terms agreed and the Reserved step is simply not drawn — rather than drawn as an
        unfinished step it will never reach, which would make every ordinary sale look incomplete for
        its whole life.
      */}
      <Card className="mb-6">
        <CardTitle hint="Where this sale stands, and the one thing it is waiting for.">Progress</CardTitle>

        <ol className="mb-4" data-spine>
          {spine.map((step, index) => (
            <li key={step.stage} className="flex gap-3" data-step={step.stage} data-state={step.done ? "done" : step.current ? "current" : "ahead"}>
              <span className="flex flex-col items-center">
                {/*
                  The mark. Filled for what is done, ringed for where the sale stands, hollow for
                  what is ahead — three states drawn as one shape rather than three icons, so the
                  column reads as a line somebody is moving along.
                */}
                <span
                  className={`mt-1 grid size-4 shrink-0 place-items-center rounded-full border-2 ${
                    step.done
                      ? "border-brand-600 bg-brand-600"
                      : step.current
                        ? "border-brand-600 bg-white"
                        : "border-ink-200 bg-white"
                  }`}
                >
                  {step.done ? <span className="size-1.5 rounded-full bg-white" /> : null}
                </span>
                {index < spine.length - 1 ? (
                  <span className={`w-0.5 flex-1 ${step.done ? "bg-brand-600" : "bg-ink-200"}`} />
                ) : null}
              </span>
              <span className={`min-w-0 flex-1 ${index < spine.length - 1 ? "pb-4" : ""}`}>
                <span className={`block text-sm ${step.current ? "font-semibold text-ink-900" : "text-ink-700"}`}>
                  {step.label}
                  {/* Allocation reads "Not yet allocated" rather than drawing an empty box.
                      `purchases.plot_id` is deliberately nullable — the money can arrive before the
                      unit is chosen, sometimes by ballot — so this is an ordinary state for months
                      and is said in words. */}
                  {step.stage === "allocated" ? (
                    <span className="ml-2 text-xs font-normal text-ink-500">
                      {plot ? plot.label : `Not yet allocated`}
                    </span>
                  ) : null}
                  {step.stage === "handed_over" && purchase.handedOverOn ? (
                    <span className="ml-2 text-xs font-normal text-ink-500">{purchase.handedOverOn}</span>
                  ) : null}
                </span>
                <span className="block text-xs text-ink-500">{step.meaning}</span>
              </span>
            </li>
          ))}
        </ol>

        {/*
          The end states, said plainly.

          A defaulted sale is not a step on the spine — it has stopped moving — so the spine draws
          none of its steps as current and this says where it stopped instead.

          **Nothing happens on its own.** No unit returns to the market automatically; the two
          decisions that follow a default — return it to the market, or keep it held while the buyer
          is talked to — belong to a person, and until somebody chooses, the sale sits here visibly
          rather than quietly disappearing. Those two actions and the line-by-line settlement of what
          was paid are not yet built: they need endpoints this application does not have, and they
          are named in `.context/00-HANDOFF.md` rather than drawn here as controls that would refuse.
        */}
        {stage === "defaulted" ? (
          <Alert tone="danger" title="This sale has defaulted.">
            Nothing has been returned to the market and nothing has been refunded. Both are decisions
            for the agency, taken payment by payment — fees are ordinarily kept and instalments
            ordinarily returned, and neither happens until somebody says so.
          </Alert>
        ) : stage === "cancelled" ? (
          <Alert tone="info" title="This sale was withdrawn.">
            It is kept on the record so that the money against it can still be accounted for.
          </Alert>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {/*
              One primary action, and the rest secondary.

              `primaryActionFor` decides which, from the stage alone, so this screen and the Sales
              list cannot disagree about what a sale is waiting for.
            */}
            {primary.key === "allocate" && can("lease.edit") ? (
              <Button onClick={() => setAllocating((open) => !open)}>
                {allocating ? "Cancel" : primary.label}
              </Button>
            ) : null}
            {primary.key === "record_payment" && can("payment.record") ? (
              <Button onClick={() => setTaking((open) => !open)}>{taking ? "Cancel" : primary.label}</Button>
            ) : null}
            {primary.key === "handover" && can("lease.edit") ? (
              <Button
                disabled={handover.isPending}
                onClick={() => {
                  const on = new Date().toISOString().slice(0, 10);
                  handover.mutate({ on });
                }}
              >
                {primary.label}
              </Button>
            ) : null}
            {primary.hint ? <span className="text-xs text-ink-500">{primary.hint}</span> : null}

            <span className="ml-auto flex flex-wrap items-center gap-2">
              {/* Secondary, and deliberately quieter than the one thing the sale is waiting for. */}
              {can("payment.record") && !standing.settled && primary.key !== "record_payment" ? (
                <Button variant="ghost" size="sm" onClick={() => setTaking((open) => !open)}>
                  {taking ? "Cancel" : "Record a payment"}
                </Button>
              ) : null}
              {can("lease.edit") && primary.key !== "allocate" ? (
                <Button variant="ghost" size="sm" onClick={() => setAllocating((open) => !open)}>
                  {allocating ? "Cancel" : plot ? "Change allocation" : `Allocate a ${noun.one}`}
                </Button>
              ) : null}
              {can("invoice.edit") && purchase.lateFeeBps > 0 && standing.arrears > 0 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={lateFees.isPending}
                  onClick={() => lateFees.mutate(undefined as never)}
                >
                  Apply late payment fees
                </Button>
              ) : null}
              {can("lease.edit") && purchase.status !== "handed_over" && primary.key !== "handover" ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={handover.isPending}
                  onClick={() => {
                    const on = new Date().toISOString().slice(0, 10);
                    if (!standing.settled) {
                      if (!window.confirm(`${standing.outstandingLabel} is still outstanding. Record handover anyway?`)) return;
                      handover.mutate({ on, force: true });
                      return;
                    }
                    handover.mutate({ on });
                  }}
                >
                  Record handover
                </Button>
              ) : null}
            </span>
          </div>
        )}

        {taking ? (
          <form
            className="mt-4 grid gap-4 border-t border-ink-100 pt-4 sm:grid-cols-4"
            onSubmit={(event) => {
              event.preventDefault();
              const values = formValues(event.currentTarget);
              takePayment.mutate({
                amount: minor(String(values.amount ?? "")) ?? 0,
                method: values.method,
                receivedOn: values.receivedOn,
                reference: values.reference || null,
              });
            }}
          >
            <Field label="Amount received" htmlFor="amount" required hint="Applied to the oldest unpaid instalment first, then to the next.">
              <MoneyInput id="amount" name="amount" required placeholder="833,333" />
            </Field>
            <Field label="Payment method" htmlFor="method">
              <Select id="method" name="method" defaultValue="bank_transfer">
                <option value="bank_transfer">Bank transfer</option>
                <option value="cash">Cash</option>
                <option value="pos">POS</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
                <option value="mobile_money">Mobile money</option>
              </Select>
            </Field>
            <Field label="Date received" htmlFor="receivedOn" required>
              <Input id="receivedOn" name="receivedOn" type="date" required defaultValue={new Date().toISOString().slice(0, 10)} />
            </Field>
            <Field label="Payer's reference" htmlFor="reference">
              <Input id="reference" name="reference" placeholder="Transfer narration or teller number" />
            </Field>
            <div className="sm:col-span-4">
              <Button type="submit" disabled={takePayment.isPending}>
                {takePayment.isPending ? "Recording…" : "Record the payment"}
              </Button>
            </div>
          </form>
        ) : null}

        {allocating ? (
          <form
            className="mt-4 grid gap-4 border-t border-ink-100 pt-4 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              const values = formValues(event.currentTarget);
              allocate.mutate({ plotId: values.plotId, method: values.method });
            }}
          >
            <Field label={titleCase(noun.one)} htmlFor="plotId" required>
              <Select id="plotId" name="plotId" required defaultValue="">
                <option value="">Select a {noun.one}…</option>
                {(plots?.plots ?? [])
                  .filter((row) => row.status === "available" || row.id === plot?.id)
                  .map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.label}
                    </option>
                  ))}
              </Select>
            </Field>
            {/* Recorded because clients ask, and it is a fairness question rather than an
                administrative one. */}
            <Field label="Basis of allocation" htmlFor="method" hint="Recorded because buyers ask how allocation was determined.">
              <Select id="method" name="method" defaultValue="assigned">
                <option value="chosen">Selected by the buyer</option>
                <option value="ballot">Ballot</option>
                <option value="assigned">Assigned by the developer</option>
              </Select>
            </Field>
            <div className="flex items-end">
              <Button type="submit" disabled={allocate.isPending}>
                {allocate.isPending ? "Allocating…" : "Allocate"}
              </Button>
            </div>
          </form>
        ) : null}
      </Card>

      {/* --------------------------------------------------------- the schedule */}

      <Card padded={false} className="mb-6">
        <div className="border-b border-ink-200 p-4">
          <CardTitle
            hint={
              purchase.earlyDiscountBps > 0 && data.settlement.discount > 0
                ? `Settling the rest today would cost ${data.settlement.payableLabel} — ${data.settlement.discountLabel} off what is not yet due.`
                : "Every payment on this schedule, in order of due date."
            }
          >
            The plan
          </CardTitle>
        </div>
        <Table
          head={
            <tr>
              <th>Payment</th>
              <th>Due</th>
              <th className="num">Amount</th>
              <th className="num">Received</th>
              <th>Status</th>
            </tr>
          }
        >
          {data.instalments.map((instalment) => (
            <tr key={instalment.id}>
              <td className="font-medium">
                {instalment.label}
                {instalment.waivedReason ? <div className="text-xs text-ink-500">{instalment.waivedReason}</div> : null}
              </td>
              <td className="text-ink-600">{instalment.dueDate}</td>
              <td className="num">
                {instalment.amountLabel}
                {instalment.lateFee > 0 ? <div className="text-xs text-danger-600">Includes a late payment fee</div> : null}
              </td>
              <td className="num text-ink-600">{instalment.paidLabel}</td>
              <td>
                <Badge tone={TONE[instalment.status]}>{instalment.status.replace("_", " ")}</Badge>
              </td>
            </tr>
          ))}
        </Table>
      </Card>

      {/* ------------------------------------------------------- the paperwork */}

      {/*
        The papers of the sale.

        **A sale runs on documents the buyer expects** — an offer letter on reservation, a survey
        plan, and a deed of assignment at handover — and a developer who keeps them in a folder on
        a laptop is a developer who cannot produce one when it is asked for. They are private to
        the agency and the buyer: there is no public link to any of them, and there is not meant to
        be.
      */}
      <Card padded={false} className="mb-6">
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-200 p-4">
          <CardTitle hint="The offer letter, the survey plan and the deed. Private to this agency and the buyer — none of these is ever published.">
            Documents
          </CardTitle>
          {can("lease.edit") ? (
            <div className="ml-auto flex flex-wrap items-end gap-2">
              <Field label="What it is" htmlFor="documentKind">
                <Select
                  id="documentKind"
                  className="w-56"
                  value={documentKind}
                  onChange={(event) => setDocumentKind(event.target.value)}
                >
                  {SALE_DOCUMENT_KINDS.map((code) => (
                    <option key={code} value={code}>
                      {SALE_DOCUMENT_INFO[code].label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button variant="secondary" disabled={attaching} onClick={() => fileInput.current?.click()}>
                <IconUpload size={15} />
                {attaching ? "Attaching…" : "Attach"}
              </Button>
            </div>
          ) : null}
        </div>

        {/* The hint for whatever is selected, so the picker is not five words with no meaning. */}
        {can("lease.edit") ? (
          <p className="border-b border-ink-100 px-4 py-2 text-xs text-ink-500">
            {SALE_DOCUMENT_INFO[documentKind as keyof typeof SALE_DOCUMENT_INFO]?.hint} PDF, JPEG, PNG or WebP, up to
            15MB.
          </p>
        ) : null}

        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          hidden
          onChange={(event) => attach(event.target.files)}
        />

        {documentProblem ? (
          <div className="p-4">
            <Alert tone="danger">{documentProblem}</Alert>
          </div>
        ) : null}

        {!paperwork?.documents.length ? (
          <p className="p-6 text-center text-sm text-ink-500">
            Nothing attached yet. The offer letter can be produced from this purchase’s own figures — download it,
            sign it by hand, and attach the signed copy here.
          </p>
        ) : (
          <Table
            head={
              <tr>
                <th>Document</th>
                <th>Type</th>
                <th>Attached</th>
                <th />
              </tr>
            }
          >
            {paperwork.documents.map((document) => (
              <tr key={document.id}>
                <td className="font-medium">
                  <span className="flex items-center gap-2">
                    <IconContract size={16} />
                    {document.name}
                    {document.sizeBytes ? (
                      <span className="text-xs font-normal text-ink-400">{fileSize(document.sizeBytes)}</span>
                    ) : null}
                  </span>
                </td>
                <td className="text-ink-600">{document.kindLabel}</td>
                <td className="text-ink-600">
                  {document.createdAt.slice(0, 10)}
                  {/* Who put it there, under when. On a signed copy this is the whole of the trail
                      there is — nothing here witnessed the signing, so the account that attached
                      the file is the only name the record can honestly carry. */}
                  <span className="block text-xs text-ink-400">
                    by {document.uploadedByName ?? "somebody no longer on the account"}
                  </span>
                </td>
                <td className="text-right">
                  <span className="inline-flex gap-1">
                    {/* Served by an endpoint that has already resolved the session — never a path
                        anybody could guess or forward. */}
                    <a
                      className="btn btn-ghost btn-sm"
                      href={`/api/v1/purchases/${id}/documents/${document.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <IconExternal size={15} />
                      Open
                    </a>
                    {can("lease.edit") ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={removeDocument.isPending}
                        onClick={() => {
                          if (window.confirm(`Remove “${document.name}” from this sale?`)) {
                            removeDocument.mutate(document.id);
                          }
                        }}
                      >
                        <IconTrash size={15} />
                        Remove
                      </Button>
                    ) : null}
                  </span>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {/* -------------------------------------------------------- the commission */}

      {/*
        Drawn only where there is one. A sale the office made itself has no marketer, no rate and
        nothing to say — and a card of dashes on every purchase would make the ordinary sale look
        like an unfinished one.
      */}
      {data.commission ? (
        <Card className="mb-6">
          <CardTitle hint="Agreed on the day of sale and frozen here. Re-rating the property, the release or the price list moves what the next sale earns and cannot reach this one.">
            Commission
          </CardTitle>
          <dl className="grid gap-4 text-sm sm:grid-cols-4" data-commission>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink-400">Sold by</dt>
              <dd className="mt-0.5 font-medium">
                {data.commission.marketerId ? (
                  <Link className="text-brand-700 hover:underline" to={`/app/development/marketers/${data.commission.marketerId}`}>
                    {data.commission.marketerName}
                  </Link>
                ) : (
                  <span className="text-ink-500">Nobody recorded</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink-400">Rate</dt>
              <dd className="mt-0.5 font-medium">{Number(data.commission.bp / 100)}%</dd>
              <dd className="text-xs text-ink-500">as agreed on the day</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink-400">Earned when</dt>
              <dd className="mt-0.5 font-medium">{data.commission.basisLabel}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink-400">Commission in full</dt>
              <dd className="mt-0.5 font-medium">{data.commission.fullLabel}</dd>
              {/* What it has earned *so far* is per-sale on the marketer's own screen, and this
                  points at it rather than working it out a second time. */}
              {data.commission.marketerId ? (
                <dd className="text-xs text-ink-500">
                  <Link className="hover:underline" to={`/app/development/marketers/${data.commission.marketerId}`}>
                    What it has earned so far
                  </Link>
                </dd>
              ) : null}
            </div>
          </dl>
        </Card>
      ) : null}

      {/* ---------------------------------------------------------- the receipts */}

      <Card padded={false}>
        <div className="border-b border-ink-200 p-4">
          <CardTitle hint="Issued in the organisation's own receipt series.">
            Receipts
          </CardTitle>
        </div>
        {data.receipts.length === 0 ? (
          <p className="p-6 text-center text-sm text-ink-500">No payments received.</p>
        ) : (
          <Table
            head={
              <tr>
                <th>Receipt</th>
                <th>Date</th>
                <th>Method</th>
                <th className="num">Amount</th>
              </tr>
            }
          >
            {data.receipts.map((receipt) => (
              <tr key={receipt.id}>
                <td>
                  <Link className="text-brand-700 hover:underline" to={`/app/payments/${receipt.id}`}>
                    {receipt.receiptNumber}
                  </Link>
                  {/* The holding fee, named. It is genuinely the first money that arrived and it is
                      part of the price, so a receipt nobody can identify is a receipt somebody
                      queries. */}
                  {receipt.reservationId ? (
                    <div className="mt-0.5">
                      <Badge tone="brand">Holding fee</Badge>
                    </div>
                  ) : null}
                </td>
                <td className="text-ink-600">{receipt.receivedOn}</td>
                <td className="text-ink-600">{receipt.method.replace("_", " ")}</td>
                {/* The app's own formatter, not `Intl` reached for directly: the built-in prints
                    "NGN 6,000,000.00" where every other money column in the product prints
                    "₦6,000,000.00", and one column in a different notation is the one people
                    query. */}
                <td className="num">{fmt.money(receipt.amount)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
